/**
 * runMode.ts — Run Mode Phase A (G-3 / BUG-2026-0141) display helpers.
 *
 * 'attended'   — today's behavior: machine minutes pack into 8-hour (480-min)
 *                working days; weekends and stat holidays are skipped.
 * 'continuous' — the test runs 24/7 once started (e.g. Flex Wall Permeability):
 *                machine time is total elapsed and counts CALENDAR days.
 *
 * IMPORTANT: these helpers are DISPLAY-ONLY mirrors of the backend's
 * continuousSpanDays in backend/src/calculation/schedulingCalculations.ts
 * (keep the two formulas in lockstep). The Phase B engine flip is LIVE:
 * continuous types schedule in 24/7 calendar days on the backend; existing
 * plans adopt on explicit Sync/Replan only.
 */

export type TaskRunMode = 'attended' | 'continuous';

/** Default run mode — anything unset behaves exactly as today. */
export const DEFAULT_RUN_MODE: TaskRunMode = 'attended';

/** Minutes in a calendar day (24 h × 60) — continuous-mode day size. */
export const MINUTES_PER_CALENDAR_DAY = 1440;

/**
 * Calendar-day span of a continuous run: max(1, ceil(totalMinutes / 1440)).
 * Mirrors backend continuousSpanDays — e.g. 6300 → 5, 31500 → 22, 0 → 1.
 */
export function continuousSpanDays(totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return 1;
  return Math.max(1, Math.ceil(totalMinutes / MINUTES_PER_CALENDAR_DAY));
}

/** Short label for a run mode ('Attended' / 'Continuous (24/7)'). */
export function runModeLabel(mode: TaskRunMode | undefined | null): string {
  return (mode ?? DEFAULT_RUN_MODE) === 'continuous' ? 'Continuous (24/7)' : 'Attended';
}

/**
 * Per-mode helper text for a machine-minutes input:
 *   attended   → "Packs into 8h working days"
 *   continuous → "Total elapsed; ~N calendar days" (live preview when minutes known)
 */
export function minutesSemanticsHint(
  mode: TaskRunMode | undefined | null,
  minutes: number | null,
): string {
  if ((mode ?? DEFAULT_RUN_MODE) !== 'continuous') {
    return 'Packs into 8h working days';
  }
  if (minutes != null && minutes > 0) {
    const days = continuousSpanDays(minutes);
    return `Total elapsed (24/7); ~${days} calendar day${days !== 1 ? 's' : ''}`;
  }
  return 'Total elapsed (24/7) once started';
}
